import React from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from "@mui/material";
import { getMethodLabel } from "../pages/Payment/paymentNormalizer";

const fmtQ = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("es-GT", {
    style: "currency",
    currency: "GTQ",
    maximumFractionDigits: 2,
  }).format(num);
};

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("es-GT", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
    : "—";

export default function PaymentDetailTabbed({ data }) {
  const [tab, setTab] = React.useState(0);

  if (!data)
    return (
      <Typography variant="body2" color="text.secondary">
        Cargando detalle…
      </Typography>
    );

  const payments = data?.payments ?? [];
  const activePayments = payments.filter((p) => p?.["payment__is_active"] !== false);
  const credits = data?.credits ?? [];
  const receipts = data?.receipts ?? [];
  const adjustments = data?.adjustments ?? [];
  const invoice = data?.invoice ?? null;
  const invoiceTotal = Number(invoice?.total_effective ?? invoice?.total ?? 0);

  const totalPayments = activePayments.reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const totalCredits = credits.reduce((a, c) => a + (Number(c.amount) || 0), 0);
  const totalReceipts = receipts.reduce((a, r) => a + (Number(r.amount) || 0), 0);
  const totalAdjustments = adjustments.reduce((a, adj) => a + Number(adj.amount || 0), 0);

  /** Tabla por sección */
  function SectionTable({ columns, rows, totalLabel, totalValue }) {
    return (
      <TableContainer
        sx={{
          maxHeight: 420,
          borderRadius: 1.5,
          border: 1,
          borderColor: "divider",
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c}
                  sx={{
                    py: 0.75,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    bgcolor: "grey.50",
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  {c}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Box sx={{ py: 2.5, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Sin registros
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{
                    "&:nth-of-type(odd)": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  {r.cells.map((cell, i) => (
                    <TableCell
                      key={i}
                      align={i === r.cells.length - 1 ? "right" : "left"}
                      sx={{
                        py: 0.75,
                        fontSize: "0.8rem",
                        ...(i === r.cells.length - 1
                          ? { fontVariantNumeric: "tabular-nums" }
                          : {}),
                      }}
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}

            {rows.length > 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length - 1}
                  align="right"
                  sx={{
                    py: 0.75,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    bgcolor: "grey.50",
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  {totalLabel}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    py: 0.75,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                    bgcolor: "grey.50",
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  {totalValue}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* CABECERA */}
      <Box
        sx={{
          borderRadius: 1.5,
          p: 1.5,
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          Detalle de pago
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Pagos:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {fmtQ(totalPayments)}
            </Box>
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Notas de crédito:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {fmtQ(totalCredits)}
            </Box>
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Recibos:{" "}
            <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
              {fmtQ(totalReceipts)}
            </Box>
          </Typography>

          {invoice && (
            <>
              <Typography variant="body2" color="text.secondary">
                Total factura:{" "}
                <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                  {fmtQ(invoiceTotal)}
                </Box>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Saldo actual:{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color:
                      Number(invoice.balance || 0) > 0
                        ? "error.main"
                        : "success.main",
                  }}
                >
                  {fmtQ(Number(invoice.balance || 0))}
                </Box>
              </Typography>
              {Number(invoice.pending_adjustment_amount || 0) > 0 && (
                <Typography variant="body2" color="warning.main">
                  Pendiente NC:{" "}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {fmtQ(Number(invoice.pending_adjustment_amount || 0))}
                  </Box>
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* TABS */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          minHeight: 40,
          "& .MuiTab-root": {
            minHeight: 40,
            textTransform: "none",
            fontSize: "0.85rem",
            fontWeight: 500,
          },
        }}
      >
        <Tab label={`Pagos (${payments.length})`} />
        <Tab label={`Notas de crédito (${credits.length})`} />
        <Tab label={`Recibos (${receipts.length})`} />
        <Tab label={`Ajustes (${adjustments.length})`} />
      </Tabs>

      {/* TAB: PAGOS */}
      {tab === 0 && (
        <SectionTable
          columns={[
            "Método",
            "Documento",
            "Total doc.",
            "Banco",
            "Fecha",
            "Recibo",
            "Estado",
            "Monto aplicado",
            "Archivo",
          ]}
          rows={payments.map((p) => ({
            id: p.payment_id,
            cells: [
              getMethodLabel(p["payment__paymentMethod"]) || "—",
              p["payment__document__number"] || "—",
              fmtQ(p["payment__document__total"]),
              p["payment__document__bank_account__bankName"] || "—",
              fmtDate(p["payment__dateTime"]),
              p["payment__has_receipt"] ? "Sí" : "No",
              p["payment__is_active"] === false ? (
                <Chip size="small" color="error" label="ANULADO" />
              ) : (
                <Chip size="small" color="success" variant="outlined" label="ACTIVO" />
              ),
              p["payment__is_active"] === false ? "—" : fmtQ(p.amount),
              p["payment__document__paymentFile"] ? (
                <Typography
                  component="a"
                  href={p["payment__document__paymentFile"]}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    fontSize: "0.8rem",
                    textDecoration: "none",
                    color: "primary.main",
                    fontWeight: 500,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Ver
                </Typography>
              ) : (
                "—"
              ),
            ],
          }))}
          totalLabel="Total pagos activos (aplicado a esta factura)"
          totalValue={fmtQ(totalPayments)}
        />
      )}

      {/* TAB: NOTAS DE CRÉDITO */}
      {tab === 1 && (
        <SectionTable
          columns={["Número", "Fecha", "Monto"]}
          rows={credits.map((c) => ({
            id: c.credit_note_id,
            cells: [
              `NC #${c["credit_note__number"] ?? "—"}`,
              fmtDate(c["credit_note__date"]),
              fmtQ(c.amount),
            ],
          }))}
          totalLabel="Total notas de crédito"
          totalValue={fmtQ(totalCredits)}
        />
      )}

      {/* TAB: RECIBOS */}
      {tab === 2 && (
        <SectionTable
          columns={["Número", "Fecha", "Tipo", "Formato", "Monto"]}
          rows={receipts.map((r) => ({
            id: r.id,
            cells: [
              `#${r.number ?? "?"}`,
              fmtDate(r.date),
              r.receipt_type || "?",
              r.is_physical ? "Físico" : "Electrónico",
              fmtQ(r.amount),
            ],
          }))}
          totalLabel="Total recibos"
          totalValue={fmtQ(totalReceipts)}
        />
      )}

      {/* TAB: AJUSTES */}
      {tab === 3 && (
        <SectionTable
          columns={["Tipo", "Estado", "Monto", "Fecha", "Motivo"]}
          rows={adjustments.map((a) => ({
            id: a.id,
            cells: [
              a.type || "?",
              a.status === "P"
                ? (
                  <Box 
                    sx={{ 
                      px: 1,
                      py: 0.25,
                      borderRadius: 0.75,
                      bgcolor: "warning.50",
                      color: "warning.700",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      display: "inline-block",
                    }}
                  >
                    Pendiente NC
                  </Box>
                )
                : a.status === "C"
                  ? (
                    <Box 
                      sx={{ 
                        px: 1,
                        py: 0.25,
                        borderRadius: 0.75,
                        bgcolor: "success.50",
                        color: "success.700",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        display: "inline-block",
                      }}
                    >
                      Compensado
                    </Box>
                  )
                  : (a.status || "?"),
              fmtQ(Number(a.amount || 0)),
              fmtDate(a.created_at),
              a.reason || "?",
            ],
          }))}
          totalLabel="Total ajustes (informativo)"
          totalValue={fmtQ(totalAdjustments)}
        />
      )}
    </Box>
  );
}
